import apiClient from '@/lib/axios';
import type { ImageDto } from '@/components/shared/types';

/**
 * Backend DTO structure for job payment images
 */
interface JobPaymentImageApiDto {
  id: number;
  jobPaymentId: number;
  fileName: string;
  fileSize: number;
  fileSizeFormatted: string;
  contentType: string;
  createdAt: string;
  createdByName: string;
  downloadUrl: string;
}

/**
 * Get the full URL for an API download path with authentication token
 * Converts relative API paths to absolute URLs using the API base URL
 * Appends the auth token as a query parameter for image/file downloads
 */
function getApiUrl(downloadUrl: string): string {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7001';
  const token = localStorage.getItem('auth_token');
  // downloadUrl already includes /api prefix, so use it directly
  const baseUrl = `${apiBaseUrl}${downloadUrl}`;
  // Append token for authenticated image downloads
  return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
}

/**
 * Map backend DTO to frontend ImageDto
 */
function mapToImageDto(apiDto: JobPaymentImageApiDto): ImageDto {
  return {
    id: apiDto.id,
    fileName: apiDto.fileName,
    originalFileName: apiDto.fileName,
    filePath: getApiUrl(apiDto.downloadUrl),
    fileSize: apiDto.fileSize,
    mimeType: apiDto.contentType,
    uploadedAt: apiDto.createdAt,
    uploadedBy: apiDto.createdByName,
  };
}

/**
 * Get all images for a job payment
 */
export const getPaymentImages = async (
  jobId: number,
  paymentId: number
): Promise<ImageDto[]> => {
  const response = await apiClient.get<{ data: JobPaymentImageApiDto[] }>(
    `/jobs/${jobId}/payments/${paymentId}/images`
  );
  return response.data.data.map(mapToImageDto);
};

/**
 * Upload images for a job payment
 */
export const uploadPaymentImages = async (
  jobId: number,
  paymentId: number,
  files: File[]
): Promise<ImageDto[]> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await apiClient.post<{ data: JobPaymentImageApiDto[] }>(
    `/jobs/${jobId}/payments/${paymentId}/images`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data.data.map(mapToImageDto);
};

/**
 * Delete a payment image
 */
export const deletePaymentImage = async (
  jobId: number,
  paymentId: number,
  imageId: number
): Promise<void> => {
  await apiClient.delete(`/jobs/${jobId}/payments/${paymentId}/images/${imageId}`);
};
